/**
 * Mobile FAB visibility allowlist (#2079) — single shared definition of WHERE
 * the bottom-right FAB cluster's route-scoped members (help "?" FAB,
 * facilitator-only "Ask Praxis" FAB) are allowed to render on mobile.
 * Mirrors lib/publicRoutes.js's shape exactly: a plain array + a pure
 * predicate, zero React/DOM imports, so it stays usable from the
 * zero-import askPraxisFabVisibility.js pure module and from a plain node
 * vitest run (this workspace's frontend vitest has NO jsdom).
 *
 * Human-test source: mega77 staging 00907-6p5 session 138 — "Mobile: ask
 * Praxis and ? really get in the way. Let's disable them except on the
 * dashboard and org dashboard."
 *
 * AXIS (per the #1990/#2064 lineage rule — name the axis before the
 * mechanism): this is a VISIBILITY decision (should this FAB render at all,
 * given the current route + viewport), which is a different axis from
 * lib/fabStack.js's STACKING decision (given two-or-more ALREADY-visible
 * FABs, what vertical order/offset do they take within the corner). The two
 * axes cannot share a mechanism:
 *   - fabStack.js / FabStackContext.jsx's own header comments are explicit
 *     that they "never decide visibility", only position — folding this
 *     allowlist in there would violate that contract.
 *   - More concretely, a FabStack-level hide would also suppress the THIRD
 *     member of that stack, CoachBubble (`coach` slot,
 *     components/debrief/CoachBubble.jsx) — the debrief screen's PRIMARY
 *     CTA, which legitimately renders on `/chat/:id` (never `/dashboard` or
 *     `/org-dashboard`) and must keep showing there. An allowlist gating the
 *     whole stack would kill it by construction.
 * So visibility stays owned by each FAB's own eligibility computation (as it
 * already was pre-#2079 — see askPraxisFabVisibility.js's docstring), and
 * this module adds one more shared input to that computation rather than
 * reaching into the stacking layer.
 */

// Single source of truth for where the bottom-right FABs are allowed to show
// on mobile. Off this allowlist + on mobile => hidden, regardless of any
// other eligibility (role/flag/auth/chat-phase) the caller has already
// computed. Desktop is unaffected — see isFabAllowedOnMobile below.
export const FAB_MOBILE_ALLOWED_ROUTES = ['/dashboard', '/org-dashboard'];

// Matches the FAB CSS tokens' `@media (width <= 767px)` block (App.css
// :root) and VoiceChat.jsx's existing `matchMedia('(max-width: 767px)')`
// mobile check — 767, NOT TestingPanel's 768 — so the JS gate and the CSS
// stacking tokens agree on exactly the same breakpoint.
export const FAB_MOBILE_BREAKPOINT_QUERY = '(max-width: 767px)';

/**
 * @param {object} ctx
 * @param {boolean} ctx.isMobile  viewport currently matches FAB_MOBILE_BREAKPOINT_QUERY
 * @param {string} ctx.pathname   current location.pathname
 * @returns {boolean} true = FAB is allowed to render here (nothing to hide);
 *   false = mobile + off the allowlist, hide it. Always true when
 *   isMobile is false — desktop behaviour is unchanged by this gate.
 */
export function isFabAllowedOnMobile({ isMobile, pathname } = {}) {
  return !isMobile || FAB_MOBILE_ALLOWED_ROUTES.includes(pathname);
}
